import React, { useState } from 'react';
import { Mail, Send, MessageSquare, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';

const SupportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Support request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit support request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Support</h1>
        <p className="text-slate-600 dark:text-slate-400">Get help with Flowteras</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Contact Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Contact Us
            </CardTitle>
            <CardDescription>Reach out to our support team</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Email</p>
              <a 
                href="mailto:support@flowteras.com" 
                className="text-emerald-700 dark:text-emerald-400 hover:underline font-medium"
              >
                support@flowteras.com
              </a>
            </div>
            
            <div>
              <p className="text-sm text-muted-foreground mb-1">Response Time</p>
              <p className="font-medium">Within 24 hours</p>
            </div>

            <div className="pt-4 border-t">
              <Link to="/contact">
                <Button variant="outline" className="w-full">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Visit Contact Page
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Support Form Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Submit a Support Request
            </CardTitle>
            <CardDescription>Describe your issue and we'll help you out</CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-emerald-700 dark:text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  Request Submitted!
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  We've received your support request and will get back to you within 24 hours.
                </p>
                <Button onClick={() => { setSubmitted(false); setFormData({ ...formData, message: '' }); }}>
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="support-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="support-email"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company">Organization (optional)</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Your organization name"
                    data-testid="support-company"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">How can we help?</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    required
                    placeholder="Describe your issue or question..."
                    data-testid="support-message"
                  />
                </div>
                
                <Button type="submit" disabled={loading} className="w-full" data-testid="support-submit">
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Help Tips */}
      <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
        <MessageSquare className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 dark:text-blue-300">
          <strong>Tip:</strong> For faster resolution, please include relevant details like error messages, 
          steps to reproduce the issue, and screenshots if applicable.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default SupportPage;
